import { TFunction } from 'react-i18next';
import { FormikProps } from 'formik';
import { FormData, SpeakerData } from '../feature/webinar-creator-wizard';
import { StyledButton } from '@selfserve/react-ui';
import styled from '@emotion/styled';
import { MouseEvent, useCallback, useState, useMemo, useContext } from 'react';
import { SpeakerModal } from '../utils/speaker-modal';
import { KavaEventPrefix } from '@selfserve/react-utils';
import MarketingInfoContext from '../../../../react-feature-marketing-info/src/lib/utils/markerting-info-context';

interface Props {
  t: TFunction<'translation', undefined>;
  formik: FormikProps<FormData>;
}

const AddGlyph = styled.span(({ theme }: { theme?: any }) => ({
  '&:before': {
    content: '"..."',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    mask: 'no-repeat url("assets/add.svg") center',
    width: '8px',
    height: '8px',
    marginRight: '5px',
    paddingRight: '5px',
  },
}));

const RemoveButton = styled(StyledButton)(({ theme }: { theme?: any }) => ({
  width: '32px',
  minWidth: '32px',
  height: '32px',
  borderRadius: '4px',
  backgroundColor: theme.palette.common.white,
  color: theme.palette.primary.main,
  marginBottom: '0px',
  fontWeight: '600',
  '& :last-child': {
    display: 'none',
  },
  '&:hover': {
    backgroundColor: 'rgba(217, 217, 217, 1)',
  },
  '&:before': {
    content: '"..."',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    mask: 'no-repeat url("assets/fail.svg") center',
    maskSize: '12px',
    width: '12px',
    height: '12px',
  },
}));

const AddSpeakerButton = styled(StyledButton)(({ theme }: { theme?: any }) => ({
  backgroundColor: theme.palette.common.white,
  color: theme.palette.primary.main,
  fontWeight: '600',
  fontFamily: 'Lato',
  '&:hover': {
    backgroundColor: 'rgba(217, 217, 217, 1)',
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const StyledSpeakersContainer = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '256px',
  overflowY: 'auto',
  marginBottom: '24px',
  scrollbarGutter: 'stable',
  '&::-webkit-scrollbar': {
    width: '3px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    width: '3px',
    backgroundColor: '#CCCCCC',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-button': {
    width: '3px',
    height: '4px',
  },
}));

const StyledSpeakerPreview = styled.div(() => ({
  height: '64px',
  padding: '8px 0px 8px 0px',
  display: 'flex',
  alignItems: 'center',
}));

const StyledSpeakerPhoto = styled.div(
  ({ speaker }: { speaker: SpeakerData }) => ({
    width: '48px',
    height: '48px',
    borderRadius: '4px',
    marginRight: '16px',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    ...(speaker.photo
      ? {
          backgroundImage: 'url("' + speaker.photo + '")',
        }
      : {
          backgroundColor: 'rgb(112, 112, 112)',
          '&::after': {
            content: `"${speaker.firstName[0].toUpperCase()}${speaker.lastName[0].toUpperCase()}"`,
            visibility: 'visible',
            fontFamily: 'Lato',
            color: 'white',
            fontSize: '14px',
            lineHeight: '16px',
            fontWeight: '700',
            margin: '12px',
            top: 'calc(50% - 12px)',
            position: 'relative',
          },
        }),
  })
);

const StyledSpeakerDescription = styled.div(({ theme }: { theme?: any }) => ({
  lineHeight: '48px',
  flex: '1',
  display: 'flex',
  flexDirection: 'column',
  '& div': {
    fontSize: '14px',
    lineHeight: '16px',
    fontWeight: '700',
  },
  '& :last-child': {
    marginTop: '8px',
    color: theme.palette.grey['300'],
  },
}));

export const WebinarCreatorSpeakers = ({ t, formik }: Props) => {
  const {
    values: { speakers },
    setFieldValue,
  } = formik;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { raiseAnalyticEventsRequest } = useContext(MarketingInfoContext);

  const handleModalToggle = useCallback(
    () => () => {
      raiseAnalyticEventsRequest(`${KavaEventPrefix}SpeakerAdd`);
      setIsModalOpen(!isModalOpen);
    },
    [isModalOpen]
  );

  const handleRemoveSpeaker = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      raiseAnalyticEventsRequest(`${KavaEventPrefix}SpeakerRemove`);
      const index = +(event.target as HTMLInputElement).value;
      setFieldValue(
        'speakers',
        speakers.filter((obj, i) => i !== index)
      );
    },
    [speakers, setFieldValue]
  );

  const handleAdd = useCallback(
    (speaker: SpeakerData) => {
      const newSpeakers = speakers;
      newSpeakers.push(speaker);
      setFieldValue('speakers', newSpeakers);
      setIsModalOpen(false);
    },
    [speakers, setFieldValue]
  );

  const speakersEmails = useMemo(
    () => speakers.map((speaker) => speaker.email),
    [speakers]
  );

  const SpeakerElement = (speaker: SpeakerData, index: number) => {
    return (
      <StyledSpeakerPreview>
        <StyledSpeakerPhoto speaker={speaker} />
        <StyledSpeakerDescription>
          <div>{[speaker.firstName, speaker.lastName].join(' ')}</div>
          <div>{[speaker.title, speaker.company].join(', ')}</div>
        </StyledSpeakerDescription>
        <RemoveButton onClick={handleRemoveSpeaker} value={index} />
      </StyledSpeakerPreview>
    );
  };

  return (
    <>
      <AddSpeakerButton fullWidth onClick={handleModalToggle()}>
        <AddGlyph />
        {t('webinar-wizard.speakers-add')}
      </AddSpeakerButton>
      <StyledSpeakersContainer>
        {speakers.map((speaker, index) => SpeakerElement(speaker, index))}
      </StyledSpeakersContainer>

      <SpeakerModal
        open={isModalOpen}
        handleClose={handleModalToggle()}
        handleAdd={handleAdd}
        speakerEmails={speakersEmails}
      />
    </>
  );
};
